import { useEffect, useState } from 'react';

/** Minimal toast bus — replaces the web app's notistack-based toast util. */

export type ToastKind = 'success' | 'error' | 'info';

/** Optional call-to-action rendered as a button inside the toast. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** Milliseconds on screen. Defaults to 4 s; a toast that carries an action deserves longer. */
  duration?: number;
  action?: ToastAction;
}

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
}

type Listener = (toasts: Toast[]) => void;

const DEFAULT_DURATION_MS = 4_000;

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((listener) => listener(toasts));
}

function dismiss(id: number): void {
  if (!toasts.some((toast) => toast.id === id)) return;
  toasts = toasts.filter((toast) => toast.id !== id);
  notify();
}

function emit(kind: ToastKind, message: string, options: ToastOptions = {}): number {
  const id = nextId++;
  toasts = [...toasts, { id, kind, message, action: options.action }];
  notify();
  setTimeout(() => dismiss(id), options.duration ?? DEFAULT_DURATION_MS);
  return id;
}

export const toast = {
  success: (message: string, options?: ToastOptions) => emit('success', message, options),
  error: (message: string, options?: ToastOptions) => emit('error', message, options),
  info: (message: string, options?: ToastOptions) => emit('info', message, options),
  dismiss,
};

const KIND_CLASS: Record<ToastKind, string> = {
  success: 'bg-green-600',
  error: 'bg-red-600',
  info: 'bg-cf-ink',
};

/** Fixed-position toast outlet — mount once in the layout. */
export function Toaster() {
  const [items, setItems] = useState<Toast[]>(toasts);

  useEffect(() => {
    listeners.add(setItems);
    return () => {
      listeners.delete(setItems);
    };
  }, []);

  if (items.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {items.map((item) => (
        <div
          key={item.id}
          role="status"
          className={`flex items-center gap-3 rounded-md px-4 py-2 text-sm text-white shadow-lg ${KIND_CLASS[item.kind]}`}
        >
          <span>{item.message}</span>
          {item.action && (
            <button
              type="button"
              onClick={() => {
                item.action?.onClick();
                dismiss(item.id);
              }}
              className="rounded border border-white/60 px-2 py-0.5 text-xs font-semibold whitespace-nowrap hover:bg-white/15"
            >
              {item.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
